<template>
  <div>
    <div class="row justify-content-center">
      <div class="col-xl-12">
        <div class="row">
          <!-- Session Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              optionFilterProp="children"
              style="width: 100%"
              @change="handleSessionChange"
              :filterOption="false"
              :defaultValue="currentSession ? currentSession.id : ''"
            >
            <template v-for="schoolSession in schoolSessions">
              <a-select-option :value="schoolSession.id" :key="schoolSession.id">
                  {{schoolSession.name}}
                  <small class="text-primary ml-2">{{ currentSession.id === schoolSession.id ? 'Current' : '' }}</small>
              </a-select-option>
            </template>
            </a-select>
          </div>
          <!-- Class Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="classSpinning">
              <a-select
                size="large"
                v-model="selectedClass"
                placeholder="All Classes"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleClassSelect"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined">Select Class</a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                  {{schoolClass.name}}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="sectionSpinning">
              <a-select
              size="large"
                v-model="selectedSection"
                placeholder="All Sections"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleSectionChange"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="undefined">Select Section</a-select-option>
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                  {{section.name}}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <div class="col-md-6 col-12 mb-2 text-right">
            <div class="d-inline-flex flex-row">
              <div class="mr-2">
                <bulk-upload-timetable v-if="rolesetting.insertable" @uploaded="handleAfterUpload"/>
              </div>
            </div>
          </div>
        </div>

        <a-spin :spinning="loading">
          <div class="card">
            <div class="card-body">
              <div v-if="timetableData">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <div>
                      <button
                        type="button"
                        class="btn btn-light btn-sm btn-with-addon"
                        @click="download"
                      >
                        <span class="btn-addon">
                          <i class="btn-addon-icon fe fe-download" />
                        </span>
                        Download PDF
                      </button>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="text-right">
                      <a href="javascript:;" class="btn btn-sm btn-dark btn-with-addon mr-2"
                        @click="addNewColumn"
                      >
                        <span class="btn-addon">
                          <i class="btn-addon-icon fe fe-plus" />
                        </span>
                        Column
                      </a>
                      <a href="javascript:;" class="btn btn-sm btn-danger btn-with-addon"
                        @click="removeColumn"
                      >
                        <span class="btn-addon">
                          <i class="btn-addon-icon fe fe-minus" />
                        </span>
                        Last Column
                      </a>
                    </div>
                  </div>
                </div>
                <vue-custom-scrollbar
                  class="scroll-area"
                  :suppressScrollY='true'
                >
                <div id="timetableView">
                  <neo-print-wrapper title="Time Table">
                    <!-- Class, section, Session -->
                    <div class="d-none d-print-block">
                      <p>Class: <span class="font-weight-bold">{{getSelectedName(schoolClasses, selectedClass)}}</span>, Section: <span class="font-weight-bold">{{getSelectedName(sections, selectedSection)}}</span>, Session: <span class="font-weight-bold">{{getSelectedName(schoolSessions, selectedSession)}}</span></p>
                    </div>
                    <!-- Timetable -->
                    <table class="table table-sm table-bordered">
                      <thead>
                        <tr class="text-center">
                          <th scope="col">p</th>
                          <th scope="col" v-for="position in noOfColumns" :key="position">
                            <small class="font-weight-bold text-muted neo-opacity-50">{{ position }}</small>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="day in workingWeekDays" :key="day.value">
                          <th scope="row" class="bg-dark text-center" style="vertical-align: middle;">
                            <p class="m-0 text-light">{{day.name}}</p>
                          </th>
                          <td v-for="position in noOfColumns" :key="position" class="neo_timetable_cell bg-light p-1">
                            <div v-if="getTableCellData(position, day.value)">
                              <timetable-cell :cellData="getTableCellData(position, day.value)" :rolesetting="rolesetting" @updateTable="handleUpdateTable"/>
                            </div>
                            <div v-else class="text-center py-2">
                              <a
                                href="javascript:;"
                                class="p-2 bg-light text-muted neo-opacity-50"
                                @click="openAddTimetableForm(position, day.value)"
                                v-if="rolesetting.insertable"
                              >
                                <i class="fe fe-plus"></i>
                              </a>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </neo-print-wrapper>
                </div>
                </vue-custom-scrollbar>
              </div>

              <!-- If no timetable data -->
              <div v-else class="text-center my-5">
                <a-tag color="purple">Please select a Class and Section to view Timetable data.</a-tag>
              </div>
            </div>
          </div>
        </a-spin>

      </div>
    </div>
    <add-timetable-entry @created="handleUpdateTable"/>
    <edit-timetable-entry @updated="handleUpdateTable"/>
  </div>
</template>

<script>
import { find } from 'lodash'
import { workingWeekDays } from '@/services/globaldata'

import vueCustomScrollbar from 'vue-custom-scrollbar'
import NeoPrintWrapper from '@/components/layout/Print/Default'

import TimetableCell from '@/components/timetable/Display/partials/tablecell'
import AddTimetableEntry from '@/components/timetable/Form/Add/index'
import EditTimetableEntry from '@/components/timetable/Form/Edit/index'
import BulkUploadTimetable from '@/components/timetable/Bulk/index'
// import DisplayTimetable from '@/components/timetable/Display/index'

export default {
  components: {
    BulkUploadTimetable,
    TimetableCell,
    AddTimetableEntry,
    EditTimetableEntry,
    vueCustomScrollbar,
    NeoPrintWrapper,
  },
  mounted: function() {
    this.$http.get('/api/session')
      .then(({ data }) => {
        this.schoolSessions = data.data
        this.selectedSession = find(data.data, ['is_current', 1]).id
        this.getClasses()
      })
      .catch(error => {
        console.log(error)
      })
  },
  props: {
    rolesetting: { type: Object },
  },
  computed: {
    currentSession: function() {
      const session = find(this.schoolSessions, ['is_current', 1])
      return session
    },
  },
  watch: {
    currentSession: function (val) {
      this.selectedSession = val.id
    },
  },
  data: function() {
    return {
      loading: false,
      defaultNoOfColumns: 10,
      noOfColumns: 10,
      highestPosition: 10,
      data: [],
      schoolClasses: [],
      sections: [],
      schoolSessions: [],
      workingWeekDays,
      selectedClass: undefined,
      selectedSection: undefined,
      selectedSession: undefined,
      sectionSpinning: false,
      classSpinning: false,
      timetableData: null,
      pagepermission: {
        insertable: false,
        editable: false,
        viewable: false,
        deletable: false,
      },
    }
  },
  methods: {
    getClassSectionTimetable() {
      this.loading = true
      this.$http.get('/api/timetable', {
        params: {
          class_id: this.selectedClass,
          section_id: this.selectedSection,
          session_id: this.selectedSession,
        },
      })
        .then(({ data }) => {
          this.timetableData = data.data.timetable
          this.highestPosition = data.data.highest_position

          if (data.data.highest_position > this.defaultNoOfColumns) {
            console.log(data.data.highest_position)
            this.noOfColumns = data.data.highest_position + 1
          }

          this.loading = false
        })
        .catch(error => {
          this.loading = false
          console.log(error)
        })
    },
    handleUpdateTable: function() {
      this.getClassSectionTimetable()
    },
    handleAfterUpload: function() {
      this.getClassSectionTimetable()
    },
    getClasses: function() {
      this.classSpinning = true
      this.$http.get('/api/class', {
        params: {
          session_id: this.selectedSession,
        },
      })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch(error => {
          console.log(error)
        })
    },
    handleClassSelect(value) {
      this.selectedSection = undefined
      if (value) {
        this.sectionSpinning = true
        this.getClassSections(value)
        this.timetableData = null
        this.noOfColumns = this.defaultNoOfColumns
      }
    },
    getClassSections(value) {
      this.$http.get(`/api/section/class/${value}`, {
        params: {
          session_id: this.selectedSession,
        },
      })
        .then(({ data }) => {
          this.sections = data.data
          this.sectionSpinning = false
        })
        .catch(error => {
          console.log(error)
        })
    },
    handleSectionChange (value) {
      if (value) {
        this.selectedSection = value
        this.timetableData = null
        this.noOfColumns = this.defaultNoOfColumns
        this.getClassSectionTimetable()
      } else {
        this.selectedSection = undefined
        this.timetableData = null
        this.noOfColumns = this.defaultNoOfColumns
      }
    },
    handleSessionChange (value) {
      if (value) {
        this.selectedSession = value
      } else {
        this.selectedSession = this.currentSession
      }
      this.selectedSection = undefined
      this.selectedClass = undefined
      this.timetableData = null
      this.noOfColumns = this.defaultNoOfColumns

      this.getClasses()
    },
    addNewColumn() {
      if (this.noOfColumns < 25) {
        this.noOfColumns++
      }
    },
    removeColumn() {
      this.noOfColumns = this.noOfColumns - 1
    },
    openAddTimetableForm(position, day) {
      const data = {
        classId: this.selectedClass,
        sectionId: this.selectedSection,
        sessionId: this.selectedSession,
        position,
        day,
      }
      this.$eventBus.$emit('openTimetableAddEntryModal', data)
    },
    getTableCellData(position, day) {
      return find(this.timetableData[day], {
        'position': position, 'day': day,
      })
    },
    getSelectedName(data, dataId) {
      return find(data, ['id', dataId]).name
    },
    print() {
      this.$htmlToPaper('timetableView')
    },
    download() {
      const header = { headers: { 'Content-Type': 'application/jsons', Accept: 'application/json' },
        responseType: 'blob' }
      const className = (this.schoolClasses.filter(item => item.id === this.selectedClass))[0].name
      const sectionName = (this.sections.filter(item => item.id === this.selectedSection))[0].name
      const fileName = `Timetable_${className}  ${sectionName}.pdf`
      console.log('fileName', fileName)
      this.$http.post(`/api/timetable/pdf`, {
        session_id: this.selectedSession,
        class_id: this.selectedClass,
        section_id: this.selectedSection,
      }, header)
        .then(({ data }) => {
          var FILE = window.URL.createObjectURL(new Blob([data])) // new Blob([res.data], { type: 'application/json' }) // window.URL.createObjectURL(new Blob([res.data]))
          var docURL = document.createElement('a')
          docURL.href = FILE
          docURL.setAttribute('download', fileName)
          document.body.appendChild(docURL)
          docURL.click()
        })
        .catch(error => {
          console.log(error)
        })
    },
  },
}
</script>
<style lang="scss" scoped>
.neo_timetable_cell {
  width: 150px;
  vertical-align: middle!important;
}
.scroll-area {
  position: relative;
  margin: auto;
  width: 100%;
  height: 100%;
}
</style>
